const DEFAULT_BID_ASK_PCT = 50;
const DEFAULT_BID_ASK_ORDER_TYPE = "Bid-Ask%";

export function buildRepriceGroupedData(selectedRows, options = {}) {
  const {
    getTradingSymbol = (row) => row.exchange_trading_symbol || row.trading_symbol || "Unknown",
    getAccountCode = (row) => row.broker_client_code,
    getDefaultQuantity = (row) => row.net_quantity ?? row.quantity ?? 0,
    getRowKey = (row, index, symbol) =>
      row.key || `${symbol}_${getAccountCode(row) || "account"}_${index}`,
    defaultBidAskPct1 = DEFAULT_BID_ASK_PCT,
    defaultBidAskPct2 = DEFAULT_BID_ASK_PCT,
  } = options;

  if (!Array.isArray(selectedRows) || selectedRows.length === 0) {
    return {};
  }

  const grouped = {};

  selectedRows.forEach((row, index) => {
    const symbol = getTradingSymbol(row);

    if (!grouped[symbol]) {
      grouped[symbol] = {
        rows: [],
        repriceTradingSymbol: symbol,
      };
    }

    grouped[symbol].rows.push({
      ...row,
      modalId: getRowKey(row, index, symbol),
      quantity: getDefaultQuantity(row),
      bidAskPct1: defaultBidAskPct1,
      bidAskPct2: defaultBidAskPct2,
      orderType1: DEFAULT_BID_ASK_ORDER_TYPE,
      orderType2: DEFAULT_BID_ASK_ORDER_TYPE,
    });
  });

  return grouped;
}

export function collectRepriceModalIds(groupedData) {
  return Object.values(groupedData).flatMap((group) =>
    (group.rows || []).map((row) => row.modalId)
  );
}

export function buildRepriceConfirmPayload(groupedData, rowData, symbolOverrides = {}) {
  const groups = {};

  Object.entries(groupedData).forEach(([symbol, groupData]) => {
    const repriceTradingSymbol = symbolOverrides[symbol] ?? symbol;

    groups[symbol] = {
      repriceTradingSymbol,
      rows: (groupData.rows || []).map((row) => {
        const edits = rowData[row.modalId] || {};
        return {
          ...row,
          repriceTradingSymbol,
          quantity: edits.quantity ?? row.quantity,
          bidAskPct1: edits.bidAskPct1 ?? row.bidAskPct1,
          bidAskPct2: edits.bidAskPct2 ?? row.bidAskPct2,
          orderType1: edits.orderType1 ?? row.orderType1,
          orderType2: edits.orderType2 ?? row.orderType2,
        };
      }),
    };
  });

  return { groups };
}

function mapModalOrderType(orderType) {
  return orderType === "LIMIT" ? "LIMIT" : "BID_ASK";
}

function toNumber(value, fallback = 0) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function extractExchangeToken(row) {
  if (row?.exchange_token != null && row.exchange_token !== "") {
    const parsed = Number(row.exchange_token);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const keyParts = String(row?.key || "").split("_");
  if (keyParts.length >= 3) {
    const parsed = parseInt(keyParts[2], 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function buildRepricePositionsPayload(repricePayload, initiateInstrumentsBySymbol = {}) {
  const { groups } = repricePayload || {};
  if (!groups || typeof groups !== "object") {
    return { order_list: [] };
  }

  const order_list = [];

  Object.entries(groups).forEach(([nearSymbol, groupData]) => {
    const initiateSymbol = groupData.repriceTradingSymbol ?? nearSymbol;
    const initiateMeta = initiateInstrumentsBySymbol[nearSymbol] || {};

    (groupData.rows || []).forEach((row) => {
      const squareOffToken = extractExchangeToken(row);
      const sameSymbol =
        initiateSymbol === nearSymbol ||
        initiateSymbol === row.exchange_trading_symbol;

      let initiateToken =
        initiateMeta.exchange_token != null && initiateMeta.exchange_token !== ""
          ? Number(initiateMeta.exchange_token)
          : null;

      if ((initiateToken == null || !Number.isFinite(initiateToken)) && sameSymbol) {
        initiateToken = squareOffToken;
      }

      order_list.push({
        broker_client_code: row.broker_client_code ?? "",
        square_off_exchange: row.exchange ?? "NSE",
        square_off_exchange_token: squareOffToken,
        square_off_order_price: toNumber(row.bidAskPct1, 0),
        square_off_order_type: mapModalOrderType(row.orderType1),
        initiate_exchange: initiateMeta.exchange ?? row.exchange ?? "NSE",
        initiate_exchange_token: initiateToken,
        initiate_order_price: toNumber(row.bidAskPct2, 0),
        initiate_order_type: mapModalOrderType(row.orderType2),
        product_type: row.product_type ?? "DELIVERY",
        order_quantity: Math.abs(toNumber(row.quantity, 0)),
      });
    });
  });

  return { order_list };
}

export { DEFAULT_BID_ASK_PCT };
